import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { Button } from "primereact/button";
import styled from "styled-components";
import { SupClass } from "../api/models/supClass.models";
import { BasicProductInfo, DialogInputs } from "../hooks/useMaterials";
import { AbstractModel } from "../api/models/abstractProduct.models";
import { EcomodClass } from "../api/models/ecomodClass.models";
import { Package } from "../api/models/package.models";
import { RadioButton } from "primereact/radiobutton";
import {
  AutoComplete,
  AutoCompleteCompleteEvent,
} from "primereact/autocomplete";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { InputText } from "primereact/inputtext";
import AddMaterialInputs from "../components/addMaterialInputs";
import SupSelection from "../components/supSelection";
import BasicProductInfoFields from "../components/basicProductInfoFields";
import {
  convertAkeneoRowsToEcomodRows,
  convertEcomodRowsToAkeneoRows,
  getDefaultValuesFromPackageValues,
  getTotalPrice,
} from "../utils/rowUtils";
import usePackageRow from "../hooks/usePackageRow";
import { useDebounce } from "primereact/hooks";
import useAbstractProduct from "../hooks/useAbstractProduct";
import { ProgressSpinner } from "primereact/progressspinner";
import { InputNumber } from "primereact/inputnumber";
import { TypeTag } from "../components/typeTag";
import useModels from "../hooks/useModels";
import { v4 as uuidv4 } from "uuid";
import {
  CreatePackageRowPayload,
  UpdatePackageRowPayload,
} from "../api/models/packageRow.models";
import { Family } from "../api/models/fullProductData.models";
import {
  AkeneoModel,
  CreateAkeneoModelPaylod,
  UpdateAkeneoModelPaylod,
} from "../api/models/akeneoModels.models";
import {
  HttpRequestStatus,
  LoadingContainer,
} from "../components/loadingContainer";
import useAssociatedAbstractModels from "../hooks/useAssociatedAbstractProducts";
import { SearchProduct } from "../api/models/searchProduct.models";
import MaterialPreviewDialog from "../dialogs/materialPreviewDialog";
import useAddChildrenToModel from "../hooks/useAddChildrenToModel";
import useCombineModelValues from "../hooks/useCombineModelValues";
import { Price } from "../api/models/prices.models";
import PriceYearSelector, {
  PriceYearNote,
} from "../components/priceYearSelector";
import useProductPackageRowPrices from "../hooks/useProductPackageRowPrices";
import { InfoDot } from "../components/infoDot";
import useSynkkaEanSearch from "../hooks/useSynkkaEanSearch";
import { confirmDialog } from "primereact/confirmdialog";
import { RowValues } from "../api/models/rowValues.models";
import { FullModel } from "../api/models/fullModel.models";

const SectionHeader = styled.h4`
  font-weight: 500;
  font-size: 20px;
  letter-spacing: -0.4px;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
  gap: 0.25rem;

  & .p-inputtext,
  & .p-inputnumber {
    width: 100%;
  }
`;

const Row = styled.div`
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;

  & > div {
    flex: 1;
    min-width: 200px;
  }
`;

const SubProductRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e0e0e0;
`;

const StickyActions = styled.div`
  display: flex;
  justify-content: flex-end;
  position: sticky;
  width: 100%;
  bottom: 0;
  background-color: #fff;
  z-index: 100;
  box-shadow: 0 -2px 5px rgba(0, 0, 0, 0.1);
  padding: 1rem 2rem;
`;

const Section = styled.section`
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 2rem;

  & .loading-ean-spinner {
    align-self: auto;
    min-width: 30px !important;
    margin-top: 5px;
    margin-right: auto;
    margin-left: 0.5rem;
    flex: none !important;
  }
`;

const AddedProductInfo = styled.span<{ $isLink?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-grow: 1;
  text-overflow: ellipsis;

  ${({ $isLink }) =>
    $isLink &&
    `
    text-decoration: underline;
    cursor: pointer;

    &:hover {
      color: var(--primary-color);
    }
  `}
`;

export interface AbstractModelWithAmount extends AbstractModel {
  amount: number;
}

interface CreateAkeneoModelFormProps {
  supClasses?: SupClass[];
  ecomodClasses?: EcomodClass[];
  selectedSearchProduct?: SearchProduct;
  onHide: () => void;
  modelData?: AkeneoModel;
  presetEAN?: string;
  handleCreateNewRowFromModel?: (inputs: {
    name: string;
    ean: string;
    model: AkeneoModel;
  }) => void;
  preview?: boolean;
  refetchSearchProducts?: () => void;
  handlePreviewChange?: (product: AbstractModelWithAmount) => void;
  initialBasicInfo?: BasicProductInfo;
  onBasicInfoChange?: (values: BasicProductInfo) => void;
  pricesData?: Price[];
  useSynkkaSearch?: boolean;
  priceYearOverride?: number | null;
  setPriceYearOverride?: (year: number | null) => void;
  priceYearOptions?: { label: string; value: number }[];
  priceDefaultYear?: number;
  isLoadingPrices?: boolean;
  packages?: Package[];
  selectedPackage?: Package | null;
  setSelectedPackage?: React.Dispatch<React.SetStateAction<Package | null>>;
  isLoadingPackages?: boolean;
  selectMaterialSelection?: "materials" | "package";
  setSelectMaterialSelection?: React.Dispatch<
    React.SetStateAction<"materials" | "package">
  >;
}

const CreateAkeneoModelForm: React.FC<CreateAkeneoModelFormProps> = ({
  supClasses,
  ecomodClasses,
  onHide,
  modelData,
  presetEAN,
  handleCreateNewRowFromModel,
  selectedSearchProduct,
  preview = false,
  refetchSearchProducts,
  handlePreviewChange,
  initialBasicInfo,
  onBasicInfoChange,
  pricesData,
  useSynkkaSearch = false,
  priceYearOverride,
  setPriceYearOverride,
  priceYearOptions = [],
  priceDefaultYear,
  isLoadingPrices = false,
  packages = [],
  selectedPackage = null,
  setSelectedPackage,
  isLoadingPackages = false,
  selectMaterialSelection = "materials",
  setSelectMaterialSelection,
}) => {
  const { t } = useTranslation();
  const [companyId, toast] = useUIStore(
    useShallow((state: UIState) => [state.companyId, state.toastRef]),
  );
  const [searchEAN, debouncedEAN, setSearchEAN] = useDebounce("", 500);
  const [addedProducts, setAddedProducts] = useState<AbstractModelWithAmount[]>(
    [],
  );
  const [productToPreview, setProductToPreview] =
    useState<AbstractModelWithAmount | null>(null);

  const [synkkaSearchEan, setSynkkaSearchEan] = useState<string>("");
  const [synkkaProductImported, setSynkkaProductImported] =
    useState<boolean>(false);
  const [synkkaImporterProductValues, setSynkkaImporterProductValues] =
    useState<RowValues[]>([]);
  const [
    synkkaImportedAssociationIdentifiers,
    setSynkkaImportedAssociationIdentifiers,
  ] = useState<string[]>([]);
  const [synkkaFullModelData, setSynkkaFullModelData] =
    useState<FullModel | null>(null);

  const [price, setPrice] = useState<number | null>(null);
  const [productPrices, setProductPrices] = useState<{ [key: string]: number }>(
    {},
  );
  const [isSup, setIsSup] = useState(false);
  const [supClassCode, setSupClassCode] = useState<string | undefined>(
    undefined,
  );
  const [packageAmount, setPackageAmount] = useState<number>(1);
  const [debouncedAmounts, setDebouncedAmounts] = useState<{
    [key: string]: number;
  }>({});

  const { getProductPrices, isPending: isLoadingProductPrices } =
    useProductPackageRowPrices();

  const containsOnlyNumbers = (str?: string) => {
    if (!str) return false;
    return /^\d*$/.test(str);
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    watch,
    reset,
    trigger,
  } = useForm<DialogInputs>({
    defaultValues: {
      name: initialBasicInfo?.name ?? (modelData?.name || ""),
      ean:
        initialBasicInfo?.ean ??
        (modelData?.ean ||
          (presetEAN && containsOnlyNumbers(presetEAN) ? presetEAN : "")),
      productNumber:
        initialBasicInfo?.productNumber ??
        (modelData?.internalReference ||
          (presetEAN && !containsOnlyNumbers(presetEAN) ? presetEAN : "")),
      responsibleToReport:
        initialBasicInfo?.responsibleToReport ??
        (modelData?.responsibleToReport !== undefined
          ? modelData.responsibleToReport
          : true),
      comment: initialBasicInfo?.comment ?? (modelData?.comment || ""),
    },
    mode: "onBlur",
  });

  const { getCombinedValues, isPending: isLoadingCombinedValues } =
    useCombineModelValues();

  const handleBlur = async () => {
    await trigger(); // Validates all fields
  };

  const handleSynkkaSearch = async (ean: string) => {
    setSynkkaSearchEan(ean);
  };

  const { synkkaProducts, fetchFullModel, isLoadingFullModel } =
    useSynkkaEanSearch({
      ean: synkkaSearchEan,
      enabled: useSynkkaSearch && searchEAN.trim() !== "",
    });

  useEffect(() => {
    if (
      useSynkkaSearch &&
      synkkaProducts.length > 0 &&
      !synkkaProductImported
    ) {
      const synkkaProduct = synkkaProducts[0];
      if (
        synkkaProduct.name !== getValues("name") &&
        synkkaProduct.family === Family.product_model
      ) {
        confirmDialog({
          header: t("text-synkka-product-import"),
          message: t("header-import-synkka-product", { ean: synkkaSearchEan }),
          icon: "pi pi-exclamation-triangle",
          acceptLabel: t("cmd-yes"),
          rejectLabel: t("cmd-no"),
          accept: async () => {
            setSynkkaProductImported(true);

            // Fetch full model
            const fullModelResult = await fetchFullModel([
              synkkaProduct.ean as string,
            ]);

            // Take first result, should always be there
            const fullModelData = fullModelResult[0];
            setSynkkaFullModelData(fullModelData);
            reset({
              name: fullModelData.model.name,
              ean: fullModelData.model.ean,
              productNumber: fullModelData.model.internalReference || "",
              responsibleToReport: true,
            });

            // If the product has package rows, convert them to ecomod rows and set in form
            const values = fullModelData.packageRows?.rowValues || [];
            const convertedValues = convertAkeneoRowsToEcomodRows(
              values,
              ecomodClasses as EcomodClass[],
              supClasses as SupClass[],
            );

            setValue("values", convertedValues || []);

            setSynkkaImporterProductValues(
              fullModelData.packageRows?.rowValues,
            );
            setSynkkaImportedAssociationIdentifiers(
              combineKeys(
                fullModelData.model.productAssociations,
                fullModelData.model.modelReverseAssociations,
              ),
            );

            trigger();
          },
        });
      }
    }
  }, [synkkaProducts, getValues, t, useSynkkaSearch]);

  useEffect(() => {
    if (modelData?.identifier) {
      getCombinedValues(
        companyId!.toString(),
        modelData?.identifier as string,
      ).then((result) => {
        console.log("result", result);
        setPrice(result.price);
      });
    }
  }, [modelData?.identifier, companyId]);

  // Debounce amount changes
  useEffect(() => {
    const timeouts: { [key: string]: NodeJS.Timeout } = {};

    addedProducts.forEach((product) => {
      if (
        product.identifier &&
        debouncedAmounts[product.identifier] !== product.amount
      ) {
        if (timeouts[product.identifier]) {
          clearTimeout(timeouts[product.identifier]);
        }

        timeouts[product.identifier] = setTimeout(() => {
          setDebouncedAmounts((prev) => ({
            ...prev,
            [product.identifier]: product.amount,
          }));
        }, 500);
      }
    });

    return () => {
      Object.values(timeouts).forEach((timeout) => clearTimeout(timeout));
    };
  }, [addedProducts]);

  // Fetch combined values when debounced amount changes
  useEffect(() => {
    const fetchPrices = async () => {
      const modelPromises = addedProducts
        .filter(
          (product) =>
            product.amount > 0 && product.family === Family.product_model,
        )
        .map(async (product) => {
          if (product.identifier) {
            try {
              const result = await getCombinedValues(
                companyId!.toString(),
                product.identifier,
              );
              return {
                identifier: product.identifier,
                price: result.price * product.amount,
              };
            } catch (error) {
              console.error(
                `Error fetching combined values for ${product.identifier}:`,
                error,
              );
              return { identifier: product.identifier, price: 0 };
            }
          }
          return { identifier: product.identifier, price: 0 };
        });

      const productPromises = addedProducts
        .filter(
          (product) => product.amount > 0 && product.family === Family.product,
        )
        .map(async (product) => {
          if (product.identifier) {
            try {
              const result = await getProductPrices(
                companyId!.toString(),
                product.ean,
              );
              return {
                identifier: product.identifier,
                price:
                  result.data.length > 0
                    ? (result.data[0].packageRows?.price ?? 0) * product.amount
                    : 0,
              };
            } catch (error) {
              console.error(
                `Error fetching combined values for ${product.identifier}:`,
                error,
              );
              return { identifier: product.identifier, price: 0 };
            }
          }
          return { identifier: product.identifier, price: 0 };
        });

      const modelResults = await Promise.all(modelPromises);
      const productResult = await Promise.all(productPromises);

      const newPrices: { [key: string]: number } = {};
      productResult.concat(modelResults).forEach((result) => {
        newPrices[result.identifier] = result.price;
      });
      setProductPrices(newPrices);
    };

    if (addedProducts.length > 0 && companyId) {
      fetchPrices();
    } else {
      setProductPrices({});
    }
  }, [debouncedAmounts, companyId]);

  useEffect(() => {
    if (addedProducts.length > 0) {
      handleBlur();
    }
  }, [addedProducts]);

  useEffect(() => {
    if (preview && modelData) {
      reset({
        name: modelData.name,
        ean: modelData.ean || presetEAN || "",
        productNumber: modelData.internalReference || "",
        responsibleToReport: !!modelData.responsibleToReport || true,
        comment: modelData.comment || "",
        values: [],
      });
    }
  }, [preview, modelData]);

  const {
    abstractProductData,
    isLoadingAbstractProduct,
    setAbstractSearchEans,
  } = useAbstractProduct({
    companyId: companyId as number,
    exactMatch: true,
  });

  const {
    packageRow,
    isLoadingPackageRow,
    createNewPackageRow,
    isCreatingPackageRow,
    updateExistingPackageRow,
    isUpdatingPackageRow,
  } = usePackageRow({
    packageRowIdentifier: modelData?.packageRowsAssociation || null,
  });

  const { packageRow: selectedPackageRow } = usePackageRow({
    packageRowIdentifier: selectedPackage?.packageRowsAssociation || null,
  });

  const sortedPackages = useMemo(
    () => [...packages].sort((a, b) => a.name.localeCompare(b.name)),
    [packages],
  );

  const [filteredPackages, setFilteredPackages] =
    useState<Package[]>(sortedPackages);

  useEffect(() => {
    setFilteredPackages(sortedPackages);
  }, [sortedPackages]);

  const searchPackages = (event: AutoCompleteCompleteEvent) => {
    const query = event.query.trim().toLowerCase();
    const results = query
      ? sortedPackages.filter((pkg) => pkg.name.toLowerCase().includes(query))
      : sortedPackages;
    // always create a new array reference so the panel opens even when results are unchanged
    setFilteredPackages([...results]);
  };

  // Change the selection based on existing association when editing a model
  React.useEffect(() => {
    if (
      modelData?.packageAssociations &&
      Object.keys(modelData.packageAssociations).length > 0
    ) {
      setSelectMaterialSelection?.("package");
      const associatedPackage = packages.find(
        (pkg) => pkg.identifier in (modelData.packageAssociations as object),
      );
      setSelectedPackage?.(associatedPackage || null);
      if (associatedPackage) {
        setPackageAmount(
          modelData.packageAssociations[associatedPackage.identifier] ?? 1,
        );
      }
    }

    if (modelData?.packageRowsAssociation) {
      setSelectMaterialSelection?.("materials");
    }
  }, [modelData, packages]);

  const { addChildrenToModel, isAddingChildren } = useAddChildrenToModel({
    companyId: companyId as number,
  });

  const {
    createNewModel,
    isCreatingModel,
    updateExistingModel,
    isUpdatingModel,
  } = useModels({
    companyId: undefined,
  });

  useEffect(() => {
    if (packageRow) {
      setValue(
        "values",
        packageRow?.rowValues
          ? convertAkeneoRowsToEcomodRows(
              packageRow.rowValues,
              ecomodClasses || [],
              supClasses || [],
            )
          : [],
      );
    }
  }, [packageRow]);

  useEffect(() => {
    const searchString = debouncedEAN.trim();
    if (searchString !== "") {
      const searchStringArray = [searchString];
      setAbstractSearchEans(searchStringArray);
    }
  }, [debouncedEAN]);

  useEffect(() => {
    if (
      abstractProductData?.data &&
      Array.isArray(abstractProductData?.data) &&
      abstractProductData?.data.length > 0
    ) {
      const firstItem = abstractProductData.data[0];

      // Avoid adding the same model as being edited and adding model that has association already
      if (isSameModel(firstItem) || isLoopedStructure(firstItem)) {
        return;
      }

      if (
        (firstItem.ean || firstItem.internalReference) &&
        !addedProducts.some(
          (product) =>
            (firstItem.ean && product.ean === firstItem.ean) ||
            (firstItem.internalReference &&
              product.internalReference === firstItem.internalReference),
        )
      ) {
        setAddedProducts((prev) => [...prev, { ...firstItem, amount: 0 }]);
        // Initialize debounced amount for new product
        setDebouncedAmounts((prev) => ({
          ...prev,
          [firstItem.identifier]: 0,
        }));
      }
    }
  }, [abstractProductData]);

  const isSameModel = (productToAdd: AbstractModel) => {
    if (
      modelData?.identifier &&
      productToAdd.identifier === modelData.identifier
    ) {
      toast?.current?.show({
        severity: "warn",
        detail: t("toast-cannot-add-model-itself-as-associated-model"),
        life: 5000,
      });
      return true;
    }
    return false;
  };

  const isLoopedStructure = (productToAdd: AbstractModel) => {
    if (
      modelData?.identifier &&
      productToAdd?.modelReverseAssociations &&
      Object.keys(productToAdd.modelReverseAssociations).some(
        (key) => key === modelData.identifier,
      )
    ) {
      toast?.current?.show({
        severity: "warn",
        detail: t("toast-cannot-add-looped-structure"),
        life: 5000,
      });
      return true;
    }
    return false;
  };

  function combineKeys(
    obj1?: { [key: string]: number },
    obj2?: { [key: string]: number },
  ): string[] {
    const keys1 = obj1 ? Object.keys(obj1) : [];
    const keys2 = obj2 ? Object.keys(obj2) : [];
    return Array.from(new Set([...keys1, ...keys2]));
  }

  const { associatedAbstractModels, isLoadingAssociatedAbstractModels } =
    useAssociatedAbstractModels({
      companyId: companyId as number,
      // If synkka product is imported, show associations based on synkka imported identifiers, otherwise use model data associations
      identifiers:
        synkkaImportedAssociationIdentifiers?.length > 0
          ? synkkaImportedAssociationIdentifiers
          : modelData?.modelReverseAssociations ||
              modelData?.productAssociations
            ? combineKeys(
                modelData.modelReverseAssociations,
                modelData.productAssociations,
              )
            : undefined,
    });

  const onSubmit = async (data: DialogInputs) => {
    if (selectedSearchProduct) {
      await handleUpdateExisting(data);
    } else {
      await handleCreateNew(data);
    }
  };

  const handleCreateNew = async (data: DialogInputs) => {
    // if data has values, create packageRow and get its identifier
    let packageRowIdentifier: string | null | undefined = null;

    if (
      selectMaterialSelection === "materials" &&
      data.values &&
      data.values.length > 0
    ) {
      // Here you would typically call an API to create the package row
      // and get its identifier. For this example, we'll just use a UUID.
      packageRowIdentifier = uuidv4();
      const packageRowPayload: CreatePackageRowPayload = {
        identifier: packageRowIdentifier,
        name: data.name,
        family: Family.package_rows,
        synkkaImported: false,
        rowValues: convertEcomodRowsToAkeneoRows(data.values),
        companyId: (companyId as number).toString(),
      };
      await createNewPackageRow(packageRowPayload);
    }

    const modelAssociations = addedProducts.filter(
      (p) => p.family === Family.product_model,
    );
    const productAssociations = addedProducts.filter(
      (p) => p.family === Family.product,
    );

    const payload: CreateAkeneoModelPaylod = {
      identifier: uuidv4(),
      name: data.name,
      ean: data.ean || presetEAN || "",
      family: Family.product_model,
      companyId: (companyId as number).toString(),
      productAssociations: productAssociations.reduce(
        (acc, product) => {
          acc[product.identifier] = product.amount;
          return acc;
        },
        {} as { [key: string]: number },
      ),
      packageRowsAssociation:
        selectMaterialSelection === "materials"
          ? packageRowIdentifier || null
          : null,
      packageAssociations:
        selectMaterialSelection === "package" && selectedPackage?.identifier
          ? { [selectedPackage.identifier]: packageAmount }
          : null,
      responsibleToReport: data.responsibleToReport,
      comment: data.comment,
      internalReference: data.productNumber || "",
    };

    const result = await createNewModel({
      ...payload,
    });

    // Add children after creation
    const childrenAssociations: { [key: string]: number } = {};
    modelAssociations.forEach((model) => {
      childrenAssociations[model.identifier] = model.amount;
    });

    if (Object.keys(childrenAssociations).length > 0) {
      await addChildrenToModel(result.identifier, childrenAssociations);
    }

    refetchSearchProducts && refetchSearchProducts();

    if (handleCreateNewRowFromModel && result) {
      handleCreateNewRowFromModel({
        name: data.name,
        ean: data.ean,
        model: result,
      });
    }

    if (result) {
      onHide();
    }
  };

  const handleUpdateExisting = async (data: DialogInputs) => {
    if (!selectedSearchProduct || !modelData) return;

    let packageRowIdentifier = modelData?.packageRowsAssociation;

    if (selectMaterialSelection === "materials") {
      // Update package row if there are values and model has package row association
      if (modelData?.packageRowsAssociation && data.values.length > 0) {
        // Update existing package row:
        const packageRowPayload: UpdatePackageRowPayload = {
          identifier: packageRowIdentifier as string,
          companyId: (companyId as number).toString(),
          name: data.name,
          family: Family.package_rows,
          synkkaImported: false,
          rowValues: convertEcomodRowsToAkeneoRows(data.values),
        };
        await updateExistingPackageRow(packageRowPayload);
      }

      // If there are values but no package row association, create new package row
      if (!modelData?.packageRowsAssociation && data.values.length > 0) {
        packageRowIdentifier = uuidv4();
        const packageRowPayload: CreatePackageRowPayload = {
          identifier: packageRowIdentifier,
          name: data.name,
          family: Family.package_rows,
          synkkaImported: false,
          rowValues: convertEcomodRowsToAkeneoRows(data.values),
          companyId: (companyId as number).toString(),
        };
        await createNewPackageRow(packageRowPayload);
      }
    } else {
      packageRowIdentifier = null;
    }

    const modelAssociations = addedProducts.filter(
      (p) => p.family === Family.product_model,
    );
    const productAssociations = addedProducts.filter(
      (p) => p.family === Family.product,
    );

    const modelPayload: UpdateAkeneoModelPaylod = {
      identifier: modelData.identifier,
      name: data.name,
      ean: data.ean || presetEAN || "",
      family: Family.product_model,
      companyId: (companyId as number).toString(),
      productAssociations: productAssociations.reduce(
        (acc, product) => {
          acc[product.identifier] = product.amount;
          return acc;
        },
        {} as { [key: string]: number },
      ),
      packageRowsAssociation: packageRowIdentifier || null,
      packageAssociations:
        selectMaterialSelection === "package" && selectedPackage?.identifier
          ? { [selectedPackage.identifier]: packageAmount }
          : null,
      responsibleToReport: data.responsibleToReport,
      comment: data.comment,
      internalReference: data.productNumber || "",
    };

    const result = await updateExistingModel(modelPayload);

    const removedKeys = modelData
      ? Object.keys(modelData.modelReverseAssociations || {}).filter(
          (key) => !modelAssociations.some((model) => model.identifier === key),
        )
      : [];

    // Add children after creation
    const childrenAssociations: { [key: string]: number } = {};
    modelAssociations.forEach((model) => {
      childrenAssociations[model.identifier] = model.amount;
    });

    // Remove deleted keys by setting their amount to 0
    if (removedKeys.length > 0) {
      removedKeys.forEach((key) => {
        childrenAssociations[key] = 0;
      });
    }

    if (Object.keys(childrenAssociations).length > 0) {
      await addChildrenToModel(result.identifier, childrenAssociations);
    }

    refetchSearchProducts && refetchSearchProducts();

    if (result) {
      // Reset form and close dialog
      setValue("values", []);
      onHide();
    }
  };

  const saveDisabled = () => {
    if (selectMaterialSelection === "package") {
      return !selectedPackage || !getValues("name") || !packageAmount;
    }

    if (!!addedProducts?.length && getValues("name") && getValues("ean")) {
      return false;
    }

    const rowValues = getValues("values");

    return (
      rowValues?.length === 0 ||
      rowValues?.some((value) => !value.ecoModulationClass) ||
      rowValues?.some((value) => value.businessUse === undefined) ||
      rowValues?.some((value) => value.value === 0) ||
      !getValues("name") ||
      rowValues?.some(
        (value) =>
          !!value.secMaterial &&
          value.secValueType !== 2 &&
          (value.secValue ?? 0) > (value.value ?? 0),
      )
    );
  };

  useEffect(() => {
    if (associatedAbstractModels && associatedAbstractModels.length > 0) {
      const newModels = associatedAbstractModels.filter(
        (model) =>
          !addedProducts.some(
            (product) => product.identifier === model.identifier,
          ),
      );

      if (newModels.length > 0) {
        const modelsWithAmount: AbstractModelWithAmount[] = newModels.map(
          (model) => {
            let amount = 0;
            if (model.family === Family.product_model) {
              amount = modelData?.modelReverseAssociations
                ? modelData.modelReverseAssociations[model.identifier] || 0
                : synkkaFullModelData?.model?.modelReverseAssociations
                  ? synkkaFullModelData.model.modelReverseAssociations[
                      model.identifier
                    ]
                  : 0;
            } else if (model.family === Family.product) {
              amount = modelData?.productAssociations
                ? modelData.productAssociations[model.identifier] || 0
                : synkkaFullModelData?.model?.productAssociations
                  ? synkkaFullModelData.model.productAssociations[
                      model.identifier
                    ]
                  : 0;
            }

            return {
              ...model,
              amount,
            };
          },
        );

        setAddedProducts((prev) => [...prev, ...modelsWithAmount]);

        // Initialize debounced amounts for existing products
        const initialAmounts: { [key: string]: number } = {};
        modelsWithAmount.forEach((model) => {
          initialAmounts[model.identifier] = model.amount;
        });
        setDebouncedAmounts((prev) => ({ ...prev, ...initialAmounts }));
      }
    }

    return () => {
      setAddedProducts([]);
    };
  }, [associatedAbstractModels]);

  const materialDefaultValues = useMemo(() => {
    return getDefaultValuesFromPackageValues(
      packageRow?.rowValues || synkkaImporterProductValues || [],
      ecomodClasses || [],
      supClasses || [],
    );
  }, [packageRow, ecomodClasses, supClasses, synkkaImporterProductValues]);

  useEffect(() => {
    if (materialDefaultValues.length > 0) {
      setIsSup(!!materialDefaultValues[0].isSup);
      setSupClassCode(materialDefaultValues[0].supClass);
    }
  }, [materialDefaultValues]);

  const watchName = watch("name");
  const watchResponsibleToReport = watch("responsibleToReport");
  const watchProductNumber = watch("productNumber");
  const watchEan = watch("ean");
  const watchComment = watch("comment");

  useEffect(() => {
    onBasicInfoChange?.({
      name: watchName,
      ean: watchEan,
      productNumber: watchProductNumber,
      responsibleToReport: watchResponsibleToReport,
      comment: watchComment,
    });
  }, [
    watchName,
    watchEan,
    watchProductNumber,
    watchResponsibleToReport,
    watchComment,
    onBasicInfoChange,
  ]);

  const loading =
    isCreatingModel ||
    isAddingChildren ||
    isCreatingPackageRow ||
    isUpdatingModel ||
    isUpdatingPackageRow;

  const watchValues = watch("values");

  const basePrice = pricesData
    ? getTotalPrice(price || 0, pricesData, watchValues)
    : price || 0;

  const addedProductsPrice = Object.values(productPrices).reduce(
    (sum, price) => sum + price,
    0,
  );

  const selectedPackagePrice =
    pricesData && selectedPackageRow
      ? getTotalPrice(
          0,
          pricesData,
          convertAkeneoRowsToEcomodRows(
            selectedPackageRow.rowValues,
            ecomodClasses || [],
            supClasses || [],
          ),
        ) * packageAmount
      : 0;

  const totalPrice =
    selectMaterialSelection === "package"
      ? selectedPackagePrice + addedProductsPrice
      : basePrice + addedProductsPrice;

  const roundedPrice =
    totalPrice !== null
      ? parseFloat(totalPrice.toFixed(4)).toString() + " €"
      : "-";

  return (
    <LoadingContainer
      status={
        isLoadingAssociatedAbstractModels ||
        isLoadingPackageRow ||
        isLoadingFullModel
          ? HttpRequestStatus.LOADING
          : HttpRequestStatus.SUCCESS
      }
    >
      {productToPreview && (
        <MaterialPreviewDialog
          onHide={() => {
            setProductToPreview(null);
          }}
          ecomodClasses={ecomodClasses}
          supClasses={supClasses}
          productToPreview={productToPreview || undefined}
        />
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="form-container">
          <Section>
            <BasicProductInfoFields
              control={control}
              errors={errors}
              preview={preview}
              watchEan={watchEan}
              watchProductNumber={watchProductNumber}
              watchResponsibleToReport={watchResponsibleToReport}
              onEanBlur={() => {
                // On ean blur, also check the product number from synkka if ean matches with a product in synkka
                if (useSynkkaSearch && watchEan) {
                  handleSynkkaSearch(watchEan);
                }
              }}
              onFieldBlur={handleBlur}
              onResponsibleToReportUnchecked={() => setValue("comment", "")}
            />

            {selectMaterialSelection === "materials" && (
              <SupSelection
                isSup={isSup}
                onIsSupChange={setIsSup}
                supClass={supClassCode}
                onSupClassChange={setSupClassCode}
                supClasses={supClasses}
                disabled={preview}
              />
            )}
          </Section>

          <Row>
            <div
              style={{
                display: "flex",
                gap: "1rem",
                padding: "1rem 2.5rem 2rem 2rem",
              }}
            >
              <div>
                <RadioButton
                  inputId="model-material-selection-materials"
                  name="model-material-selection"
                  value="materials"
                  checked={selectMaterialSelection === "materials"}
                  onChange={() => setSelectMaterialSelection?.("materials")}
                  disabled={preview}
                />
                <InfoDot
                  position="right"
                  text={t("infotext-materials")}
                  marginLeft="10px"
                >
                  <label
                    htmlFor="model-material-selection-materials"
                    style={{ marginLeft: "0.5rem" }}
                  >
                    {t("label-materials")}
                  </label>
                </InfoDot>
              </div>
              <div>
                <RadioButton
                  inputId="model-material-selection-package"
                  name="model-material-selection"
                  value="package"
                  checked={selectMaterialSelection === "package"}
                  onChange={() => {
                    setSelectMaterialSelection?.("package");
                    setIsSup(false);
                    setSupClassCode(undefined);
                  }}
                  disabled={preview}
                />
                <InfoDot
                  position="right"
                  text={t("infotext-multipliable-package")}
                  marginLeft="10px"
                >
                  <label
                    htmlFor="model-material-selection-package"
                    style={{ marginLeft: "0.5rem" }}
                  >
                    {t("label-multipliable-package")}
                  </label>
                </InfoDot>
              </div>
            </div>
          </Row>

          {selectMaterialSelection === "package" && (
            <Section>
              <SectionHeader className="package-header">
                {preview
                  ? t("header-package")
                  : t("header-package-information")}
              </SectionHeader>

              <Row style={{ alignItems: "flex-end" }}>
                <InputContainer>
                  <AutoComplete
                    id="model-package-dropdown"
                    value={selectedPackage}
                    suggestions={filteredPackages}
                    completeMethod={searchPackages}
                    field="name"
                    dropdown
                    forceSelection
                    placeholder={t("label-select-package")}
                    onChange={(e) => setSelectedPackage?.(e.value)}
                    style={{ width: "100%" }}
                    disabled={isLoadingPackages || preview}
                  />
                </InputContainer>
                <InputContainer style={{ maxWidth: "200px" }}>
                  <label
                    style={{ fontSize: "12px", marginBottom: "0px" }}
                    htmlFor="model-package-amount"
                  >
                    {t("placeholder-amount")}
                  </label>
                  <InputNumber
                    id="model-package-amount"
                    value={packageAmount}
                    onChange={(e) => setPackageAmount(e.value || 1)}
                    min={1}
                    placeholder={t("placeholder-amount")}
                    disabled={isLoadingPackages || preview}
                  />
                </InputContainer>
              </Row>
            </Section>
          )}

          <Section>
            <SectionHeader className="products-header">
              <InfoDot
                position="right"
                text={t("infotext-associated-products")}
                marginLeft="10px"
              >
                {preview
                  ? t("header-preview-associated-products")
                  : t("header-associated-products")}
              </InfoDot>
            </SectionHeader>

            <Row>
              <InputContainer style={{ maxWidth: "45%" }}>
                <InputText
                  value={searchEAN}
                  onChange={(e) => {
                    setSearchEAN(e.target.value);
                  }}
                  placeholder={t("placeholder-search-by-ean")}
                  disabled={preview}
                  onBlur={handleBlur}
                />
              </InputContainer>
              {isLoadingAbstractProduct && (
                <ProgressSpinner
                  className="loading-ean-spinner"
                  style={{ width: "30px", height: "30px" }}
                />
              )}
            </Row>
            {addedProducts.map((product) => (
              <div key={product.ean}>
                <SubProductRow style={{ flexWrap: "nowrap" }}>
                  <div style={{ minWidth: "80px" }}>
                    <TypeTag type={product.family}>
                      {product.family === "product_model"
                        ? t("label-model")
                        : t("label-product")}
                    </TypeTag>
                  </div>
                  <AddedProductInfo
                    $isLink={true}
                    style={{ width: "280px" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (!preview) {
                        setProductToPreview(product);
                      } else {
                        handlePreviewChange && handlePreviewChange(product);
                      }
                    }}
                  >
                    {product.name}
                  </AddedProductInfo>
                  <AddedProductInfo style={{ marginLeft: "15px" }}>
                    {product.ean}
                  </AddedProductInfo>
                  <InputContainer>
                    <label
                      style={{ fontSize: "12px", marginBottom: "0px" }}
                      htmlFor={`amount-${product.ean}`}
                    >
                      {t("placeholder-amount")}
                    </label>
                    <InputNumber
                      style={{ width: "150px", height: "35px" }}
                      value={product.amount}
                      onChange={(e) => {
                        const newAmount = e.value || 0;
                        setAddedProducts((prev) =>
                          prev.map((p) =>
                            p.ean === product.ean
                              ? { ...p, amount: newAmount }
                              : p,
                          ),
                        );
                      }}
                      placeholder={t("placeholder-amount")}
                      min={0}
                      inputId={`amount-${product.ean}`}
                      disabled={preview}
                      onBlur={handleBlur}
                    />
                  </InputContainer>
                  {!preview && (
                    <Button
                      style={{ margin: "auto", paddingRight: "15px" }}
                      icon="pi pi-trash"
                      className="p-button-rounded p-button-text p-button-danger"
                      type="button"
                      onClick={() => {
                        setAddedProducts((prev) => {
                          const filtered = prev.filter(
                            (p) => p.ean !== product.ean,
                          );
                          return filtered;
                        });
                        // Clear debounced amounts and prices for removed product
                        setDebouncedAmounts((prev) => {
                          const updated = { ...prev };
                          delete updated[product.identifier];
                          return updated;
                        });
                        setProductPrices((prev) => {
                          const updated = { ...prev };
                          delete updated[product.identifier];
                          return updated;
                        });
                      }}
                      tooltip={t("cmd-delete")}
                      tooltipOptions={{ position: "top" }}
                    />
                  )}
                </SubProductRow>
              </div>
            ))}
          </Section>

          {selectMaterialSelection === "materials" && (
            <Section>
              <SectionHeader className="material-header">
                {preview ? t("header-materials") : t("label-add-material")}
              </SectionHeader>

              {!isLoadingPackageRow && (
                <AddMaterialInputs
                  setReportRowValues={(values) => {
                    setValue("values", values);
                    if (values?.length > 0) {
                      handleBlur();
                    }
                  }}
                  ecomodClasses={ecomodClasses}
                  defaultValues={materialDefaultValues}
                  supClasses={supClasses}
                  preview={preview}
                  isSup={isSup}
                  supClass={supClassCode}
                />
              )}
            </Section>
          )}

          <Section>
            <SectionHeader>{t("header-combined-prices")}</SectionHeader>
            <LoadingContainer
              status={
                isLoadingCombinedValues ||
                isLoadingProductPrices ||
                isLoadingPrices
                  ? HttpRequestStatus.LOADING
                  : HttpRequestStatus.SUCCESS
              }
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  whiteSpace: "nowrap",
                }}
              >
                <span>
                  {t("label-combined-price-per-model-unit")}: {roundedPrice}{" "}
                  {t("label-vat-amount")}
                  {priceYearOverride && (
                    <PriceYearNote>
                      {`(${priceYearOverride} ${t("label-prices-from-year")})`}
                    </PriceYearNote>
                  )}
                </span>
                {setPriceYearOverride && (
                  <PriceYearSelector
                    priceYearOverride={priceYearOverride ?? null}
                    setPriceYearOverride={setPriceYearOverride}
                    priceYearOptions={priceYearOptions}
                    defaultYear={priceDefaultYear ?? new Date().getFullYear()}
                  />
                )}
              </div>
            </LoadingContainer>
          </Section>
        </div>

        <StickyActions>
          <Button
            role="cancel"
            type="button"
            label={t("cmd-cancel")}
            className="p-button-text"
            onClick={onHide}
            disabled={loading}
          />
          {!preview && (
            <Button
              style={{ marginRight: "4rem" }}
              type="submit"
              label={t("cmd-save")}
              className="p-ml-2"
              disabled={loading || saveDisabled()}
              loading={loading}
            />
          )}
        </StickyActions>
      </form>
    </LoadingContainer>
  );
};

export default CreateAkeneoModelForm;
